import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Inject,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { getCurrencySymbol } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Discount, DiscountType } from '@galaxy/billing';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideGalaxyDateAdapter } from '@vendasta/galaxy/datepicker';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  getTimezoneOffsetInMilliseconds,
  getUTCEndOfDay,
  getUTCStartOfDay,
  getUTCTime,
} from '@vendasta/galaxy/utility/date-utils';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

export type DiscountForRetailDialog = Partial<Discount> & Pick<Discount, 'merchantId' | 'customerId' | 'SKU' | 'id'>;
export type DiscountFromRetailDialog = DiscountForRetailDialog & Required<Pick<Discount, 'discountType' | 'amount'>>;
export type OnSaveResponse = Promise<{ success: boolean; discount: DiscountFromRetailDialog }>;

export interface DiscountDialogInfo {
  discount: DiscountForRetailDialog;
  currencyCode: string;
  mode: 'edit' | 'create';
  onSave: (discount: DiscountFromRetailDialog, mode: 'edit' | 'create') => OnSaveResponse;
  hideDates?: boolean;
}

const AMOUNT_VALIDATORS = [Validators.required, Validators.min(0.01), Validators.pattern('^[0-9.,-]*$')];
const PERCENT_VALIDATORS = [Validators.required, Validators.min(1), Validators.max(100), Validators.pattern('^[0-9]*$')]; // only whole numbers
const RETAIL_DISCOUNT_TYPE_OPTIONS = [DiscountType.FIXED_AMOUNT_PER_UNIT, DiscountType.PERCENT_AMOUNT];
const DISCOUNT_DURATION_OPTIONS = {
  TEMPORARY: 'temporary',
  PERMANENT: 'permanent',
};

@Component({
  selector: 'billing-ui-add-discount-modal',
  imports: [
    MatDialogModule,
    MatButtonModule,
    GalaxyFormFieldModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatDatepickerModule,
    GalaxyButtonLoadingIndicatorModule
],
  providers: [...provideGalaxyDateAdapter()],
  templateUrl: './retail-discount-dialog.component.html',
  styleUrls: ['./retail-discount-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RetailDiscountDialogComponent implements OnInit {
  formGroup: UntypedFormGroup;

  fixedAmountPerUnitDiscountType = DiscountType.FIXED_AMOUNT_PER_UNIT;
  minimumEndDate: WritableSignal<Date> = signal(new Date());

  currencySymbol = getCurrencySymbol(this.data.currencyCode, 'wide');

  isLoading = signal(false);
  private _destroyRef: DestroyRef = inject(DestroyRef);

  now = new Date();
  constructor(
    public dialogRef: MatDialogRef<RetailDiscountDialogComponent>,
    private translateService: TranslateService,
    private snackbarService: SnackbarService,
    private formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: DiscountDialogInfo,
  ) {}

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      description: new FormControl<string>(this.data.discount?.description || '', [Validators.required]),
      discountType: new UntypedFormControl(this.data.discount?.discountType || this.fixedAmountPerUnitDiscountType, [
        Validators.required,
        isValidDiscountTypeForRetail,
      ]),
      amount: new FormControl<string>((0).toFixed(2), AMOUNT_VALIDATORS),
      percentAmount: new FormControl<number>(0, PERCENT_VALIDATORS),
      startDate: new FormControl<Date>(this.now, [Validators.required]),
      endDate: new FormControl<Date | null>(null),
      duration: new UntypedFormControl(DISCOUNT_DURATION_OPTIONS.TEMPORARY, [Validators.required]),
    });

    if (this.data.discount.discountType === DiscountType.PERCENT_AMOUNT) {
      this.percentAmount.patchValue(this.data.discount.amount ?? 0, {
        emitEvent: false,
      });
    } else {
      const amountValue = this.data.discount.amount ?? 0;
      this.amount.patchValue(
        typeof amountValue === 'number' ? amountValue.toFixed(2) : parseFloat(amountValue).toFixed(2),
        {
          emitEvent: false,
        },
      );
    }

    this.duration.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value) => {
      // If we are setting the date to forever - then that assumes it starts now
      if (value === DISCOUNT_DURATION_OPTIONS.PERMANENT) {
        this.startDate.patchValue(smallestDate(this.startDate.value, this.now), { emitEvent: false });
        // Clear endDate when switching to permanent
        this.endDate.patchValue(null, { emitEvent: false });
      }
      // Update validation based on duration
      this.updateEndDateValidation(value);
    });

    this.startDate.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((val) => {
      this.minimumEndDate.set(val);
    });

    this.discountType.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((val) => {
      if (val === DiscountType.FIXED_AMOUNT_PER_UNIT) {
        this.amount.patchValue((0).toFixed(2), { emitEvent: false });
      }
      if (val === DiscountType.PERCENT_AMOUNT) {
        this.percentAmount.patchValue(0, { emitEvent: false });
      }
      this.syncAmountValidatorsToDiscountType(val);
    });

    if (this.data.mode === 'edit') {
      this.prefillValues(this.data.discount);
    }

    this.syncAmountValidatorsToDiscountType(this.discountType.value);

    // Set initial validation for endDate based on duration
    this.updateEndDateValidation(this.duration.value);
  }

  private getValidationErrorKey(): string {
    if (this.discountType.value === DiscountType.PERCENT_AMOUNT && this.percentAmount.hasError('min')) {
      return 'BILLING_SUBSCRIPTIONS.CREATE_SUBSCRIPTIONS.DISCOUNTS.PERCENT_MUST_BE_POSITIVE';
    }
    if (this.discountType.value === DiscountType.FIXED_AMOUNT_PER_UNIT && this.amount.hasError('min')) {
      return 'BILLING_SUBSCRIPTIONS.CREATE_SUBSCRIPTIONS.DISCOUNTS.AMOUNT_MUST_BE_POSITIVE';
    }
    return 'BILLING_SUBSCRIPTIONS.CREATE_SUBSCRIPTIONS.DISCOUNTS.ERROR_MISSING_INFORMATION';
  }

  private syncAmountValidatorsToDiscountType(discountType: DiscountType): void {
    if (discountType === DiscountType.PERCENT_AMOUNT) {
      this.percentAmount.setValidators(PERCENT_VALIDATORS);
      this.amount.clearValidators();
    } else {
      this.amount.setValidators(AMOUNT_VALIDATORS);
      this.percentAmount.clearValidators();
    }
    this.amount.updateValueAndValidity({ emitEvent: false });
    this.percentAmount.updateValueAndValidity({ emitEvent: false });
  }

  get description(): FormControl<string> {
    return this.formGroup.get('description') as UntypedFormControl;
  }

  get amount(): FormControl<string> {
    return this.formGroup.get('amount') as UntypedFormControl;
  }

  get percentAmount(): FormControl<number> {
    return this.formGroup.get('percentAmount') as UntypedFormControl;
  }

  get duration(): UntypedFormControl {
    return this.formGroup.get('duration') as UntypedFormControl;
  }

  get discountType(): UntypedFormControl {
    return this.formGroup.get('discountType') as UntypedFormControl;
  }

  get startDate(): UntypedFormControl {
    return this.formGroup.get('startDate') as UntypedFormControl;
  }

  get endDate(): UntypedFormControl {
    return this.formGroup.get('endDate') as UntypedFormControl;
  }

  formatPrice(control: AbstractControl | null) {
    if (!control) return;
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      control.setValue((0).toFixed(2), { emitEvent: false });
      return;
    }
    const numValue = parseFloat(value.toString().replace(/[^0-9.-]/g, ''));
    if (!isNaN(numValue) && numValue >= 0) {
      control.setValue(numValue.toFixed(2), { emitEvent: false });
    } else {
      control.setValue((0).toFixed(2), { emitEvent: false });
    }
  }

  private updateEndDateValidation(duration: string): void {
    // If hideDates is true, no validation on endDate
    if (this.data.hideDates) {
      this.endDate.clearValidators();
    } else if (duration === DISCOUNT_DURATION_OPTIONS.TEMPORARY) {
      this.endDate.setValidators([Validators.required]);
    } else {
      this.endDate.clearValidators();
    }
    this.endDate.updateValueAndValidity();
  }

  private prefillValues(discount: Partial<Discount>): void {
    this.description.patchValue(discount.description ?? '', {
      emitEvent: false,
    });
    this.discountType.patchValue(discount.discountType || this.fixedAmountPerUnitDiscountType, {
      emitEvent: false,
    });
    if (discount.discountType === DiscountType.FIXED_AMOUNT_PER_UNIT) {
      const amountValue = (discount.amount ?? 0) / 100; // convert from cents
      this.amount.patchValue(amountValue.toFixed(2), {
        emitEvent: false,
      });
    } else {
      this.percentAmount.patchValue(discount.amount ?? 0, {
        emitEvent: false,
      });
    }

    if (discount.start) {
      // need to convert discount start & end timestamps to UTC since at this point the value is the user's local time
      // (eg. converting to UTCStartOfDay could mean the wrong day)
      this.startDate.patchValue(adjustDateToForm(discount.start), {
        emitEvent: false,
      });
    }

    let hasEndDate = false;
    const zeroDate = new Date('0001-01-01T00:00:00Z').valueOf();
    if (discount.end && discount.end.valueOf() !== zeroDate) {
      hasEndDate = true;
      this.endDate.patchValue(adjustDateToForm(discount.end), {
        emitEvent: false,
      });
    }

    // If there is no end date and the description is not empty, then set the duration to permanent
    if (!hasEndDate && this.description.value !== '') {
      this.duration.patchValue(DISCOUNT_DURATION_OPTIONS.PERMANENT, {
        emitEvent: false,
      });
    }
  }

  async onSave(): Promise<void> {
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      this.snackbarService.openErrorSnack(this.translateService.instant(this.getValidationErrorKey()));
      return;
    }
    this.isLoading.set(true);
    // calculate discount amount according to the discount type
    let amount = parseFloat(this.amount.value || '0');
    if (this.discountType.value === DiscountType.FIXED_AMOUNT_PER_UNIT) {
      amount = Math.round(amount * 100); // stored in cents
    } else {
      amount = this.percentAmount.value;
    }
    let end: Date | undefined = undefined;
    if (this.endDate?.value && this.duration.value === DISCOUNT_DURATION_OPTIONS.TEMPORARY) {
      end = getUTCEndOfDay(adjustDateFromForm(this.endDate.value));
    }
    const start = getUTCStartOfDay(adjustDateFromForm(this.startDate.value));

    const discount: DiscountFromRetailDialog = {
      ...this.data.discount,
      discountType: this.discountType.value,
      amount,
      start,
      end: end,
      description: this.description.value,
    };
    if (this.data.onSave) {
      const resp = await this.data.onSave(discount, this.data.mode);
      if (resp.success) {
        this.dialogRef.close(resp.discount);
      }
    } else {
      this.dialogRef.close(false);
    }
    this.isLoading.set(false);
  }
}

function isValidDiscountTypeForRetail() {
  return (control: UntypedFormControl) => {
    if (RETAIL_DISCOUNT_TYPE_OPTIONS.findIndex(control.value) === -1) {
      return { invalidDiscountType: true };
    }
    return null;
  };
}

function adjustDateToForm(d: Date): Date {
  return getUTCTime(d);
}

function adjustDateFromForm(d: Date): Date {
  return new Date(d.getTime() - getTimezoneOffsetInMilliseconds());
}

function smallestDate(d1: Date, d2: Date): Date {
  if (d1.getTime() < d2.getTime()) {
    return d1;
  }
  return d2;
}
